import { useApolloClient } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { orderBy, uniqBy } from "lodash";

import { InventoryChangeReason, IProduct } from "__graphql__/types";
import { InventoryProduct } from "flows/Inventory/shared/models/inventoryProduct/types";
import {
  GetProductsByShelfDocument,
  GetProductsByShelfQuery,
  GetProductsByShelfQueryVariables,
  SearchProductsByEanDocument,
  SearchProductsByEanQuery,
  SearchProductsByEanQueryVariables,
  SearchProductsByTextDocument,
  SearchProductsByTextQuery,
  SearchProductsByTextQueryVariables,
} from "flows/Inventory/shared/queries/product/product.generated";
import {
  UpdateProductStockByDeltaAndMultipleReasonsDocument,
  UpdateProductStockByDeltaAndMultipleReasonsMutation,
  UpdateProductStockByDeltaAndMultipleReasonsMutationVariables,
} from "shared/queries/inventoryEntry/inventoryEntry.generated";
import { isShelfNumberFormat } from "utils/scan";

import { UpdateProductStockSuccessPayload } from "./types";
import { StockCorrectionState } from "./useStockCorrectionStore";
import { serializeInventoryProductFromSearchResult } from "../../../shared/models/inventoryProduct/serializer";

export function useStockCorrectionServices() {
  const client = useApolloClient();

  const fetchProductsByShelf = async (shelfNumber: string): Promise<InventoryProduct[]> => {
    const { data: dataProductsByShelf } = await client.query<
      GetProductsByShelfQuery,
      GetProductsByShelfQueryVariables
    >({
      query: GetProductsByShelfDocument,
      variables: { input: { shelfNumber } },
      fetchPolicy: "no-cache",
    });

    const filteredResults = dataProductsByShelf.getInventoryEntriesByShelf.inventoryEntries
      .filter((inventoryEntry) => inventoryEntry.product)
      .map((inventoryEntry) => {
        return serializeInventoryProductFromSearchResult(inventoryEntry.product as IProduct);
      });
    return orderBy(filteredResults, (result) => result.stock, "desc");
  };

  const fetchProductsByEan = async (ean: string): Promise<InventoryProduct[]> => {
    const { data: dataProductsByEan } = await client.query<
      SearchProductsByEanQuery,
      SearchProductsByEanQueryVariables
    >({
      query: SearchProductsByEanDocument,
      variables: { searchProductsByEanInput: { ean }, isAlwaysInbound: true },
      fetchPolicy: "no-cache",
    });
    return dataProductsByEan.searchUnitsByEan.units.map((unit) =>
      serializeInventoryProductFromSearchResult(unit.product),
    );
  };

  const fetchProducts = async (barcode: string): Promise<InventoryProduct[]> => {
    try {
      if (isShelfNumberFormat(barcode)) {
        return await fetchProductsByShelf(barcode);
      }
      return await fetchProductsByEan(barcode);
    } catch (error) {
      datadogRum.addError(new Error(`Error: searchProductsByEan ${error}`));
      throw new Error("Error: searchProductsByEan");
    }
  };

  const fetchProductsByText = async (query: string): Promise<InventoryProduct[]> => {
    const { data } = await client.query<
      SearchProductsByTextQuery,
      SearchProductsByTextQueryVariables
    >({
      query: SearchProductsByTextDocument,
      variables: { searchProductsByTextInput: { query } },
      fetchPolicy: "no-cache",
    });

    const filteredUnits = data.searchUnitsByText.units.filter((unit) => !!unit.product);
    const uniqueProducts = uniqBy(filteredUnits, (unit) => unit.product.sku);

    return uniqueProducts.map((unit) => serializeInventoryProductFromSearchResult(unit.product));
  };

  const updateProductStock = async (
    context: StockCorrectionState,
    event: {
      initial: number;
      reason: InventoryChangeReason | null;
      counter: number;
      newTotalStock: number;
    },
  ): Promise<UpdateProductStockSuccessPayload> => {
    const selectedInventoryUnit = context.inventoryProductsList.find(
      (inventoryUnit) => inventoryUnit.productSku === context.selectedInventoryProductSku,
    );
    const { stockCorrectionId } = context;
    try {
      if (!selectedInventoryUnit) {
        throw new Error("Error: inventoryUnit not provided in context");
      }
      const { productSku } = selectedInventoryUnit;

      const stockUpdates = [];

      if (event.reason && event.counter) {
        stockUpdates.push({
          quantityDelta:
            event.reason === InventoryChangeReason.correction ? event.counter : event.counter * -1,
          reason: event.reason,
          transactionId: `${productSku}:${event.reason}:${event.counter}:${Date.now()}`,
          stockCorrectionId,
        });
      }

      await client.mutate<
        UpdateProductStockByDeltaAndMultipleReasonsMutation,
        UpdateProductStockByDeltaAndMultipleReasonsMutationVariables
      >({
        mutation: UpdateProductStockByDeltaAndMultipleReasonsDocument,
        variables: {
          updateProductStockByDeltaAndMultipleReasonsInput: {
            sku: productSku,
            stockUpdates,
          },
        },
      });

      return {
        before: event.initial,
        productSku: selectedInventoryUnit.productSku,
        after: event.newTotalStock ?? 0,
        reason: event.reason,
        counter: event.counter,
      };
    } catch (error) {
      datadogRum.addError(new Error(`Error: updateProductStock ${error}`));
      throw new Error("Error: updateProductStock");
    }
  };

  return {
    fetchProducts,
    updateProductStock,
    fetchProductsByText,
  };
}
